import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import bs58 from "bs58";
import type { OperatorObject } from "@altheia-xyz/types";
import { api } from "@/lib/api";

interface AuthState {
  operator: OperatorObject | null;
  token: string | null;
  status: "hydrating" | "idle" | "signing" | "ready" | "error";
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => void;
  setOperator: (op: OperatorObject) => void;
}

const AuthContext = createContext<AuthState | null>(null);

const STORAGE_KEY = "altheia.session.v1";

interface PersistedSession {
  token: string;
  wallet: string;
  exp: number; // unix seconds
}

/**
 * Decode a JWT payload (no signature verification — just to read exp client-side).
 * Returns null if the token is malformed.
 */
function readJwtExp(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

function loadPersisted(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    if (!parsed.token || !parsed.wallet) return null;
    const now = Math.floor(Date.now() / 1000);
    if (parsed.exp && parsed.exp <= now) {
      // Expired — drop it
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persist(session: PersistedSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* localStorage might be disabled — graceful degradation, just don't persist */
  }
}

function clearPersisted(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* same as above */
  }
}

export function AltheiaAuthProvider({ children }: { children: ReactNode }) {
  const { publicKey, signMessage, connected, disconnect } = useWallet();
  const [operator, setOperator] = useState<OperatorObject | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("hydrating");
  const [error, setError] = useState<string | null>(null);
  // Tracks whether we've finished the initial localStorage hydration. After
  // hydration, the wallet-connection effect can fire signIn() — but not before
  // (otherwise we'd race the persisted session and double-prompt Phantom).
  const hydratedRef = useRef(false);
  // Remembers the wallet from the persisted session. Used by the wallet-effect
  // to know "the expected wallet" during the window where status is "ready"
  // but the operator object hasn't loaded from /operator/me yet. Without this,
  // autoConnect fires before the operator fetch completes, the effect sees
  // operator?.wallet_pubkey === undefined, treats it as a mismatch, and clears
  // the session — the exact bug we were trying to fix.
  const persistedWalletRef = useRef<string | null>(null);

  // Hydrate from localStorage on mount. This is what makes reload not re-prompt.
  //
  // Strategy: optimistically restore the session (status = "ready") so the UI
  // works immediately. Verify the token in the background. ONLY clear the
  // session on explicit auth rejection (401/403) — never on network errors,
  // 500s, or anything else. A backend hiccup must not log the user out.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const persisted = loadPersisted();
      if (!persisted) {
        if (!cancelled) {
          setStatus("idle");
          hydratedRef.current = true;
        }
        return;
      }

      // Optimistic restore — set the token + status before verification.
      // Stash the wallet in a ref so the wallet-effect can do mismatch checks
      // even before /operator/me populates the full operator object.
      setToken(persisted.token);
      setStatus("ready");
      persistedWalletRef.current = persisted.wallet;
      hydratedRef.current = true;

      // Background verify. Failure of this call does not affect UI state
      // unless the server explicitly says "your token is bad".
      try {
        const fresh = await api.getOperatorMe(persisted.token);
        if (!cancelled) setOperator(fresh);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        // Only clear on auth rejection. Network errors / 5xx → keep session.
        if (msg.startsWith("401") || msg.startsWith("403")) {
          clearPersisted();
          setToken(null);
          setOperator(null);
          setStatus("idle");
        }
        // else: keep optimistic session. UI works; next successful API call
        // will populate `operator` once backend is reachable again.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async () => {
    if (!publicKey || !signMessage) {
      setError("wallet not ready");
      return;
    }
    setStatus("signing");
    setError(null);
    try {
      const wallet = publicKey.toBase58();
      const { challenge } = await api.authChallenge(wallet);
      const messageBytes = new TextEncoder().encode(challenge);
      const signatureBytes = await signMessage(messageBytes);
      const signature = bs58.encode(signatureBytes);
      const verified = await api.authVerify({ wallet, challenge, signature });
      setToken(verified.token);
      setOperator(verified.operator);
      setStatus("ready");
      persistedWalletRef.current = wallet;
      const exp = readJwtExp(verified.token) ?? Math.floor(Date.now() / 1000) + 86_400;
      persist({ token: verified.token, wallet, exp });
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : "sign-in failed");
    }
  }, [publicKey, signMessage]);

  const signOut = useCallback(() => {
    setOperator(null);
    setToken(null);
    setStatus("idle");
    setError(null);
    clearPersisted();
    persistedWalletRef.current = null;
    void disconnect();
  }, [disconnect]);

  // Wallet-connection effect:
  //   - Don't act until hydration finishes (otherwise we'd double-prompt the
  //     wallet for a sign on the same load as a perfectly valid persisted JWT).
  //   - Session lives in JWT (persisted to localStorage), not in wallet state.
  //     A disconnected wallet does NOT invalidate the session — we just can't
  //     sign new transactions until the user reconnects. Action buttons (Pause,
  //     Revoke, Edit Policy, Withdraw) require `connected` from useWallet().
  //   - We DO clear the session when a DIFFERENT wallet connects (account
  //     switch) — that's a legitimate identity change.
  useEffect(() => {
    if (!hydratedRef.current) return;
    // Gate on signMessage too — after disconnect/reconnect, Phantom can briefly
    // expose publicKey before re-attaching signMessage. Firing signIn() in that
    // window throws (or hits the "wallet not ready" early return) and leaves
    // the gate in an error state until full page refresh.
    if (!connected || !publicKey || !signMessage) return;

    const walletStr = publicKey.toBase58();
    // The "expected wallet" for this session. Falls back to the persisted
    // wallet during the window between hydration and /operator/me completing.
    const expectedWallet = operator?.wallet_pubkey ?? persistedWalletRef.current;

    // Already signed in for THIS wallet — nothing to do.
    if (status === "ready" && expectedWallet === walletStr) return;
    // Different wallet connected → drop stale session, re-sign with the new one.
    if (status === "ready" && expectedWallet && expectedWallet !== walletStr) {
      setOperator(null);
      setToken(null);
      persistedWalletRef.current = null;
      clearPersisted();
      void signIn();
      return;
    }
    // Idle + wallet connected → first-time sign-in for this wallet.
    if (status === "idle") {
      void signIn();
    }
  }, [connected, publicKey, signMessage, status, operator, signIn]);

  return (
    <AuthContext.Provider
      value={{ operator, token, status, error, signIn, signOut, setOperator }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AltheiaAuthProvider");
  return ctx;
}
